import type { PdpMedia, PdpVariant } from "~/server/api/schemas/pdp";

export interface GalleryMedia extends PdpMedia {
  /** Resolved on the server so the client gallery never imports storage config. */
  url: string | null;
  /** Resolved poster URL for VIDEO rows; null for images / posterless video. */
  posterUrl: string | null;
}

/**
 * Derive the active media set for the selected variant (P3-03).
 *
 * Per-COLOUR, not per-variant: an asset tagged with a `colourValueId` shows only
 * when the selected variant carries that option value; untagged assets (null)
 * are shared and always shown. When the product has no colour-tagged media the
 * set is the full ordered list — so single-colour and legacy products are
 * unaffected. The resolved variant's `imageId` is then floated to the first/LCP
 * slide so the viewer opens on the colour the shopper picked.
 */
export function mediaForVariant(
  media: GalleryMedia[],
  variant: PdpVariant | undefined,
): GalleryMedia[] {
  const hasColourTags = media.some((item) => item.colourValueId !== null);
  const selectedValues = new Set(variant?.optionValueIds ?? []);

  const set =
    hasColourTags && variant
      ? media.filter(
          (item) =>
            item.colourValueId === null ||
            selectedValues.has(item.colourValueId),
        )
      : media;

  if (!variant?.imageId) return set;

  const selected = set.find((item) => item.id === variant.imageId);
  if (!selected) return set;

  return [selected, ...set.filter((item) => item.id !== selected.id)];
}

export type VideoSource =
  { kind: "file"; src: string } | { kind: "embed"; src: string };

/** Convert supported hosted video URLs to no-autoplay embeds. */
export function videoSource(value: string): VideoSource {
  try {
    const url = new URL(value);
    const host = url.hostname.replace(/^www\./, "");

    if (host === "youtu.be") {
      const id = url.pathname.split("/").filter(Boolean)[0];
      if (id)
        return {
          kind: "embed",
          src: `https://www.youtube-nocookie.com/embed/${id}`,
        };
    }
    if (host === "youtube.com" || host === "m.youtube.com") {
      const id =
        url.searchParams.get("v") ??
        url.pathname.match(/^\/shorts\/([^/]+)/)?.[1];
      if (id)
        return {
          kind: "embed",
          src: `https://www.youtube-nocookie.com/embed/${id}`,
        };
    }
    if (host === "vimeo.com") {
      const id = url.pathname.split("/").filter(Boolean)[0];
      if (id)
        return { kind: "embed", src: `https://player.vimeo.com/video/${id}` };
    }
  } catch {
    // The admin schema validates URLs; retaining the value is the safest fallback.
  }

  return { kind: "file", src: value };
}
